import React from "react";
import { SummaryFieldItems, SummaryFields } from "./SummaryFields";
import { Trans } from "@lingui/macro";
import {
  Box,
  Container,
  Divider,
  Heading,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ParticipantFullSummary } from "../../types/participant.types";
import { formatDateForInput } from "../../utils/date-formatter";
import { CheckIcon } from "@chakra-ui/icons";
import { Badge } from "../preview/Badge";
import { FaRegAddressCard, FaMoneyBillWave } from "react-icons/fa";
import CurrencyContext from "../../context/CurrencyContext";
import { Currency, Language } from "../../types/conference.types";
import { ActivitySummary } from "./ActivitySummary";
import { ParticipantSummaryPresentationSection } from "./ParticipantSummaryPresentationSection";
import { ParticipantSummaryAttendanceFeeSection } from "./ParticipantSummaryAttendanceFeeSection";
import { ParticipantSummaryEscortFeeSection } from "./ParticipantSummaryEscortFeeSection";
import { ParticipantSummaryAccommodationSection } from "./ParticipantSummaryAccommodationSection";
import {
  formatAddressType,
  formatPaymentType,
  formatTitle,
} from "../../utils/type-formatter";
import { useLingui } from "@lingui/react";
import { FinalFee } from "./FinalFee";
import { NO_VALUE } from "../../utils/constants";
import { formatPrice } from "../../utils/price-formatter";
import { CongressButtonScheme } from "../common/CongressButton";
import { RegistrationRequest } from "../../types/registration.types";
import { useParticipantChanges } from "../../hooks/useParticipantChanges";
import {
  useListActiveAdditionalFieldsQuery,
  useListAdditionalFieldsByCongressIdQuery,
} from "../../store/additional-fields/additional-fields";
import type { Fee } from "../../types/fee.types";
import type { Accomodation } from "../../types/accomodation.types";

interface ParticipantSummaryProps {
  registrationId: number;
  participant: ParticipantFullSummary;
  modifiedData?: RegistrationRequest | null;
  showChanges?: boolean;
}

export const ParticipantSummary: React.FC<ParticipantSummaryProps> = ({
  registrationId,
  participant,
  modifiedData,
  showChanges = false,
}) => {
  const {
    changes,
    displayConfigJson: configJson,
    modifiedMeals,
    modifiedProfessionals,
    modifiedSocials,
    modifiedPresentations,
    modifiedTotalPrice,
    totalPriceChanged,
    allParticipantFees,
    allAccomodations,
  } = useParticipantChanges(
    participant,
    modifiedData,
    showChanges,
    registrationId,
  );
  const { i18n } = useLingui();
  const congressId = participant.congressEvent.id;
  // Update módban minden field-et lekérünk (aktív és nem aktív), regisztráció módban csak az aktívakat
  const isEditMode = showChanges || !!modifiedData;
  const { data: activeCustomFields = [] } = useListActiveAdditionalFieldsQuery(
    congressId,
    { skip: isEditMode },
  );
  const { data: allCustomFields = [] } =
    useListAdditionalFieldsByCongressIdQuery(congressId, { skip: !isEditMode });
  const customFields = isEditMode ? allCustomFields : activeCustomFields;
  const currency = React.useMemo(() => {
    return participant.congressEvent.currency === Currency.HUF
      ? participant.congressEvent.language === Language.HUNGARIAN
        ? "Ft"
        : "HUF"
      : "EUR";
  }, [participant]);

  const participantItems = React.useMemo((): SummaryFieldItems[] => {
    if (!participant) return [];

    const nameParts = participant.fullName?.split(" ") || [];
    const originalFirstName = nameParts.length > 0 ? nameParts[0] : "";
    const originalSurName = nameParts.slice(1).join(" ") || "";

    const items: SummaryFieldItems[] = [
      {
        icon: CheckIcon,
        title: <Trans>Registration number</Trans>,
        text: String(participant.registrationNumber),
        full: true,
      },
      {
        icon: CheckIcon,
        title: <Trans>Title</Trans>,
        text: formatTitle(participant.title) || NO_VALUE,
        isChanged: changes["participantRegister.title"] !== undefined,
        originalValue: changes["participantRegister.title"]?.original
          ? formatTitle(changes["participantRegister.title"].original)
          : undefined,
        modifiedValue: changes["participantRegister.title"]?.modified
          ? formatTitle(changes["participantRegister.title"].modified)
          : undefined,
      },
      {
        icon: CheckIcon,
        title: <Trans>Name</Trans>,
        text: participant.fullName,
        isChanged:
          changes["participantRegister.firstName"] !== undefined ||
          changes["participantRegister.surName"] !== undefined,
        originalValue:
          changes["participantRegister.firstName"]?.original ||
          changes["participantRegister.surName"]?.original
            ? `${
                changes["participantRegister.firstName"]?.original ||
                originalFirstName
              } ${
                changes["participantRegister.surName"]?.original ||
                originalSurName
              }`
            : undefined,
        modifiedValue:
          changes["participantRegister.firstName"]?.modified ||
          changes["participantRegister.surName"]?.modified
            ? `${
                changes["participantRegister.firstName"]?.modified ||
                originalFirstName
              } ${
                changes["participantRegister.surName"]?.modified ||
                originalSurName
              }`
            : undefined,
      },
      {
        icon: CheckIcon,
        title: <Trans>Workplace</Trans>,
        text: participant.workplace || NO_VALUE,
        isChanged: changes["participantRegister.workplace"] !== undefined,
        originalValue: changes["participantRegister.workplace"]?.original,
        modifiedValue: changes["participantRegister.workplace"]?.modified,
      },
      {
        icon: CheckIcon,
        title: <Trans>Email</Trans>,
        text: participant.email || NO_VALUE,
        isChanged: changes["participantRegister.email"] !== undefined,
        originalValue: changes["participantRegister.email"]?.original,
        modifiedValue: changes["participantRegister.email"]?.modified,
      },
    ];

    // Get original custom field values
    const originalCustomFieldValues = participant.customFieldValues.reduce(
      (acc, field) => {
        acc[field.fieldName] = field.valueText;
        return acc;
      },
      {} as Record<string, string>,
    );

    // Get modified custom field values from modifiedData
    const modifiedCustomFieldValues: Record<string, string> = {};
    if (
      modifiedData?.participantRegister.valuesByFieldId &&
      customFields.length > 0
    ) {
      modifiedData.participantRegister.valuesByFieldId.forEach(
        (value, fieldId) => {
          const field = customFields.find((f) => f.id === fieldId);
          if (field) {
            modifiedCustomFieldValues[field.fieldName] = String(value);
          }
        },
      );
    }

    // Combine original and modified values, prioritizing modified if exists
    const allCustomFieldNames = new Set([
      ...Object.keys(originalCustomFieldValues),
      ...Object.keys(modifiedCustomFieldValues),
    ]);

    const customFieldItems: SummaryFieldItems[] = Array.from(
      allCustomFieldNames,
    ).map((fieldName) => {
      const originalValue = originalCustomFieldValues[fieldName];
      const modifiedValue = modifiedCustomFieldValues[fieldName];
      const hasChange =
        showChanges &&
        modifiedData &&
        modifiedValue !== undefined &&
        modifiedValue !== originalValue;

      return {
        icon: CheckIcon,
        title: fieldName,
        text:
          modifiedValue !== undefined
            ? modifiedValue
            : originalValue || NO_VALUE,
        isChanged: hasChange || false,
        originalValue: hasChange ? originalValue : undefined,
        modifiedValue: hasChange ? modifiedValue : undefined,
      };
    });

    return [...items, ...customFieldItems];
  }, [participant, changes, modifiedData, showChanges, customFields]);

  const addressItems = React.useMemo(() => {
    if ((!participant || !participant.address) && !modifiedData?.address)
      return [];
    const addressCustomFieldValues = (
      participant?.address?.customFieldValues || []
    ).reduce(
      (acc, field) => {
        acc[field.fieldName] = field.valueText;
        return acc;
      },
      {} as Record<string, string>,
    );

    return [
      {
        icon: FaRegAddressCard,
        title: <Trans>Type of address</Trans>,
        text: participant?.address?.addressType
          ? i18n._(formatAddressType(participant.address.addressType))
          : NO_VALUE,
        isChanged: changes["address.addressType"] !== undefined,
        originalValue: changes["address.addressType"]?.original
          ? i18n._(formatAddressType(changes["address.addressType"].original))
          : NO_VALUE,
        modifiedValue: changes["address.addressType"]?.modified
          ? i18n._(formatAddressType(changes["address.addressType"].modified))
          : NO_VALUE,
      },
      {
        title: "",
        text: "",
      },
      {
        icon: FaRegAddressCard,
        title: <Trans>Country</Trans>,
        text: participant.address?.country || NO_VALUE,
        isChanged: changes["address.country"] !== undefined,
        originalValue: changes["address.country"]?.original ?? NO_VALUE,
        modifiedValue: changes["address.country"]?.modified,
      },
      {
        icon: FaRegAddressCard,
        title: <Trans>Postal code</Trans>,
        text: participant.address?.postalCode || NO_VALUE,
        isChanged: changes["address.postalCode"] !== undefined,
        originalValue: changes["address.postalCode"]?.original ?? NO_VALUE,
        modifiedValue: changes["address.postalCode"]?.modified,
      },
      {
        icon: FaRegAddressCard,
        title: <Trans>City</Trans>,
        text: participant.address?.city || NO_VALUE,
        isChanged: changes["address.city"] !== undefined,
        originalValue: changes["address.city"]?.original ?? NO_VALUE,
        modifiedValue: changes["address.city"]?.modified,
      },
      {
        icon: FaRegAddressCard,
        title: <Trans>Address</Trans>,
        text: participant.address?.street || NO_VALUE,
        isChanged: changes["address.street"] !== undefined,
        originalValue: changes["address.street"]?.original ?? NO_VALUE,
        modifiedValue: changes["address.street"]?.modified,
      },
      {
        icon: FaRegAddressCard,
        title: <Trans>Mobile</Trans>,
        text: participant.address?.phone || NO_VALUE,
        isChanged: changes["address.phone"] !== undefined,
        originalValue: changes["address.phone"]?.original ?? NO_VALUE,
        modifiedValue: changes["address.phone"]?.modified,
      },
      ,
      ...(Object.keys(addressCustomFieldValues).map((key: string) => ({
        icon: CheckIcon,
        title: key,
        text: addressCustomFieldValues[key] || NO_VALUE,
      })) as SummaryFieldItems[]),
    ] as SummaryFieldItems[];
  }, [participant, i18n, changes]);

  const paymentItems = React.useMemo((): SummaryFieldItems[] => {
    if (!participant && !modifiedData?.payment) return [];
    const paymentSource = modifiedData?.payment ?? participant?.payment;
    if (!paymentSource) return [];
    const paymentCustomFieldValues = (
      participant?.payment?.customFieldValues ?? []
    ).reduce(
      (acc, field) => {
        acc[field.fieldName] = field.valueText;
        return acc;
      },
      {} as Record<string, string>,
    );

    return [
      {
        icon: FaMoneyBillWave,
        title: <Trans>Payment</Trans>,
        text: paymentSource.payment
          ? i18n._(formatPaymentType(paymentSource.payment))
          : NO_VALUE,
        isChanged: changes["payment.payment"] !== undefined,
        originalValue: changes["payment.payment"]?.original
          ? i18n._(formatPaymentType(changes["payment.payment"].original))
          : NO_VALUE,
        modifiedValue: changes["payment.payment"]?.modified
          ? i18n._(formatPaymentType(changes["payment.payment"].modified))
          : NO_VALUE,
      },
      {
        title: "",
        text: "",
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Billing name</Trans>,
        text: paymentSource.fullName || NO_VALUE,
        isChanged: changes["payment.fullName"] !== undefined,
        originalValue: changes["payment.fullName"]?.original || NO_VALUE,
        modifiedValue: changes["payment.fullName"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>VAT ID number</Trans>,
        text: paymentSource.taxNumber || NO_VALUE,
        isChanged: changes["payment.taxNumber"] !== undefined,
        originalValue: changes["payment.taxNumber"]?.original || NO_VALUE,
        modifiedValue: changes["payment.taxNumber"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Country</Trans>,
        text: paymentSource.country || NO_VALUE,
        isChanged: changes["payment.country"] !== undefined,
        originalValue: changes["payment.country"]?.original || NO_VALUE,
        modifiedValue: changes["payment.country"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Postal code</Trans>,
        text: paymentSource.postalCode || NO_VALUE,
        isChanged: changes["payment.postalCode"] !== undefined,
        originalValue: changes["payment.postalCode"]?.original || NO_VALUE,
        modifiedValue: changes["payment.postalCode"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>City</Trans>,
        text: paymentSource.city || NO_VALUE,
        isChanged: changes["payment.city"] !== undefined,
        originalValue: changes["payment.city"]?.original || NO_VALUE,
        modifiedValue: changes["payment.city"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Address</Trans>,
        text: paymentSource.street || NO_VALUE,
        isChanged: changes["payment.street"] !== undefined,
        originalValue: changes["payment.street"]?.original || NO_VALUE,
        modifiedValue: changes["payment.street"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Email</Trans>,
        text: paymentSource.email || NO_VALUE,
        isChanged: changes["payment.email"] !== undefined,
        originalValue: changes["payment.email"]?.original || NO_VALUE,
        modifiedValue: changes["payment.email"]?.modified,
      },
      {
        icon: FaMoneyBillWave,
        title: <Trans>Mobile</Trans>,
        text: paymentSource.phone || NO_VALUE,
        isChanged: changes["payment.phone"] !== undefined,
        originalValue: changes["payment.phone"]?.original || NO_VALUE,
        modifiedValue: changes["payment.phone"]?.modified,
      },
      ,
      ...(Object.keys(paymentCustomFieldValues).map((key: string) => ({
        icon: CheckIcon,
        title: key,
        text: paymentCustomFieldValues[key] || NO_VALUE,
      })) as SummaryFieldItems[]),
    ] as SummaryFieldItems[];
  }, [i18n, participant, modifiedData, changes]);

  const registrationConfigJson = participant.congressEvent.configJson;

  const hasMailingAddressData = !!(
    (participant.address && participant.address.addressType) ||
    (modifiedData?.address && modifiedData?.address.addressType)
  );
  const showMailingAddressSection =
    hasMailingAddressData ||
    configJson.mailingDetailsEnabled ||
    registrationConfigJson.mailingDetailsEnabled;

  const hasBillingSummaryData = !!(
    (participant.payment && participant.payment.payment) ||
    (modifiedData?.payment &&
      (modifiedData.payment.payment ||
        modifiedData.payment.city ||
        modifiedData.payment.fullName))
  );

  const showMealsSection = !!(
    participant.meals?.some((m) => (m.choiceCount ?? 0) > 0) ||
    (participant.meals?.length ?? 0) > 0 ||
    modifiedMeals?.some((m) => (m.choiceCount ?? 0) > 0)
  );

  const showProfessionalActivitiesSection = !!(
    participant.professionals?.some((p) => (p.choiceCount ?? 0) > 0) ||
    (participant.professionals?.length ?? 0) > 0 ||
    modifiedProfessionals?.some((p) => (p.choiceCount ?? 0) > 0)
  );

  const showSocialActivitiesSection = !!(
    participant.socials?.some((s) => (s.choiceCount ?? 0) > 0) ||
    (participant.socials?.length ?? 0) > 0 ||
    modifiedSocials?.some((s) => (s.choiceCount ?? 0) > 0)
  );

  const showCommentSection = participant.comment !== null;

  const showTotalFeeSection = !!(
    configJson.participantFeeEnabled ||
    configJson.lunchEnabled ||
    configJson.activityEnabled ||
    configJson.accommodationEnabled ||
    participant.hotelBooking != null ||
    (participant.escortFee?.numberOfAttendants ?? 0) > 0 ||
    (participant.participationFee?.totalPrice ?? 0) > 0 ||
    participant.meals?.some((m) => (m.choiceCount ?? 0) > 0) ||
    participant.professionals?.some((p) => (p.choiceCount ?? 0) > 0) ||
    participant.socials?.some((s) => (s.choiceCount ?? 0) > 0)
  );

  const showModifiedTotalPrice =
    totalPriceChanged && modifiedTotalPrice !== null;

  return (
    <CurrencyContext.Provider value={currency}>
      <>
        <Stack spacing={4} as={Container} maxW={"3xl"} textAlign={"center"}>
          <Heading fontSize={"3xl"} data-testid="event-title">
            {participant.congressEvent.name}
          </Heading>
          <Text color={"gray.600"} fontSize={"xl"}>
            {formatDateForInput(participant.congressEvent.startDate)} -{" "}
            {formatDateForInput(participant.congressEvent.endDate)}
          </Text>
          <Text fontSize="lg" color={"gray.500"}>
            <Trans>Your submission summary:</Trans>
          </Text>
        </Stack>
        <Badge color="green">
          <Trans>Participant information</Trans>
        </Badge>
        <SummaryFields items={participantItems} componentKey="participant" />
        <Divider />
        {showMailingAddressSection && (
          <>
            <Badge color="purple">
              <Trans>Mailing address</Trans>
            </Badge>
            <SummaryFields
              items={addressItems}
              iconColor="purple"
              componentKey="address"
            />
            <Divider />
          </>
        )}
        {hasBillingSummaryData && (
          <>
            <Badge color="red">
              <Trans>Billing address</Trans>
            </Badge>
            <SummaryFields
              items={paymentItems}
              iconColor="red"
              componentKey="payment"
            />
            <Divider />
          </>
        )}
        <ParticipantSummaryPresentationSection
          registrationId={registrationId}
          participant={participant}
          modifiedData={modifiedData}
          displayConfigJson={configJson}
          changes={changes}
          modifiedPresentations={modifiedPresentations}
        />
        <ParticipantSummaryAttendanceFeeSection
          participant={participant}
          modifiedData={modifiedData}
          displayConfigJson={configJson}
          changes={changes}
          allParticipantFees={allParticipantFees as Fee[]}
        />
        <ParticipantSummaryEscortFeeSection
          participant={participant}
          modifiedData={modifiedData}
          displayConfigJson={configJson}
          changes={changes}
          allParticipantFees={allParticipantFees as Fee[]}
        />
        <ParticipantSummaryAccommodationSection
          participant={participant}
          modifiedData={modifiedData}
          displayConfigJson={configJson}
          changes={changes}
          allAccomodations={allAccomodations as Accomodation[]}
        />
        {showMealsSection && (
          <>
            <Badge color="gray">
              <Trans>Meals</Trans>
            </Badge>
            {(() => {
              if (changes["meals"] !== undefined && modifiedMeals) {
                return (
                  <ActivitySummary
                    activitySummaries={modifiedMeals}
                    color="gray"
                    originalSummaries={participant.meals || null}
                  />
                );
              }
              return (
                <ActivitySummary
                  activitySummaries={modifiedMeals || participant.meals || []}
                  color="gray"
                />
              );
            })()}
            <Divider />
          </>
        )}
        {showProfessionalActivitiesSection && (
          <>
            <Badge color="blue">
              <Trans>Professional Activities</Trans>
            </Badge>
            {(() => {
              if (
                changes["professionals"] !== undefined &&
                modifiedProfessionals
              ) {
                return (
                  <ActivitySummary
                    activitySummaries={modifiedProfessionals}
                    color="blue"
                    originalSummaries={participant.professionals || null}
                  />
                );
              }
              return (
                <ActivitySummary
                  activitySummaries={
                    modifiedProfessionals || participant.professionals || []
                  }
                  color="blue"
                />
              );
            })()}
            <Divider />
          </>
        )}
        {showSocialActivitiesSection && (
          <>
            <Badge color="red">
              <Trans>Social Activities</Trans>
            </Badge>
            {(() => {
              if (changes["socials"] !== undefined && modifiedSocials) {
                return (
                  <ActivitySummary
                    activitySummaries={modifiedSocials}
                    color="red"
                    originalSummaries={participant.socials || null}
                  />
                );
              }
              return (
                <ActivitySummary
                  activitySummaries={
                    modifiedSocials || participant.socials || []
                  }
                  color="red"
                />
              );
            })()}
            <Divider />
          </>
        )}
        {showCommentSection && (
          <>
            <Badge color="cyan">
              <Trans>Comment</Trans>
            </Badge>
            <Box
              borderRadius={changes["comment"] !== undefined ? "lg" : "none"}
              p={changes["comment"] !== undefined ? 2 : 0}
              _before={
                changes["comment"] !== undefined
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: "0px",
                      zIndex: -1,
                      borderRadius: "lg",
                      filter: "blur(4px)",
                      pointerEvents: "none",
                      boxShadow: `
                        -3px -3px 8px rgba(245, 101, 101, 0.15),
                        1.5px -2px 9px rgba(237, 100, 166, 0.15),
                        -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                        1.5px  3px 8px rgba(237, 100, 166, 0.125),
                        -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                        3px -3px 9px rgba(237, 137,  54, 0.125)
                      `,
                    }
                  : undefined
              }
              zIndex={0}
              position="relative"
            >
              {changes["comment"] !== undefined ? (
                <>
                  <Text as="span" color="green.500" fontWeight={600}>
                    {changes["comment"].modified}
                  </Text>
                  <Text
                    as="span"
                    color="red.500"
                    ml={2}
                    textDecoration="line-through"
                  >
                    ({participant.comment})
                  </Text>
                </>
              ) : (
                <Text>{participant.comment}</Text>
              )}
            </Box>
            <Divider />
          </>
        )}
        {showTotalFeeSection && (
          <>
            <Badge color="purple">
              <Trans>Total Fee</Trans>
            </Badge>
            <Box
              borderRadius={totalPriceChanged ? "lg" : "none"}
              _before={
                totalPriceChanged
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: "0px",
                      zIndex: -1,
                      borderRadius: "lg",
                      filter: "blur(6px)",
                      pointerEvents: "none",
                      boxShadow: `
                        -12px -12px 20px rgba(245, 101, 101, 0.3),
                        6px  -8px 22px rgba(237, 100, 166, 0.3),
                        -18px   6px 24px rgba(237, 137,  54, 0.3),
                        6px  12px 20px rgba(237, 100, 166, 0.25),
                        -12px  22px 20px rgba(245, 101, 101, 0.25),
                        12px -12px 22px rgba(237, 137,  54, 0.25)
                      `,
                    }
                  : undefined
              }
              zIndex={0}
              position="relative"
            >
              {showModifiedTotalPrice ? (
                <Stack spacing={2}>
                  <Stack
                    pb={5}
                    alignItems={"center"}
                    justifyContent={{
                      base: "flex-start",
                      md: "space-around",
                    }}
                    direction={{
                      base: "column",
                      md: "row",
                    }}
                  >
                    <HStack
                      width={{
                        base: "100%",
                        md: "40%",
                      }}
                      textAlign={"left"}
                      alignItems={"baseline"}
                    >
                      <Heading size={"lg"}>
                        <Trans>Total</Trans>
                      </Heading>
                      <Heading
                        size={"lg"}
                        bgGradient={CongressButtonScheme.GREEN_SCHEME}
                        bgClip="text"
                      >
                        <Trans>fee</Trans>
                      </Heading>
                    </HStack>
                    <Stack
                      width={{
                        base: "100%",
                        md: "60%",
                      }}
                    >
                      <Heading
                        textAlign={{ base: "left", md: "right" }}
                        size={"xl"}
                        data-testid="final-fee"
                      >
                        <Text as="span" color="green.500" fontWeight={600}>
                          {formatPrice(modifiedTotalPrice)} {currency}
                        </Text>
                        <Text
                          as="span"
                          color="red.500"
                          ml={2}
                          textDecoration="line-through"
                          fontSize="lg"
                        >
                          ({formatPrice(participant.totalPrice || 0)} {currency}
                          )
                        </Text>
                      </Heading>
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                <FinalFee totalPrice={participant.totalPrice} />
              )}
            </Box>
            <Divider />
          </>
        )}
      </>
    </CurrencyContext.Provider>
  );
};
